import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAccount } from '../components/AccountLayout';
import { CustomPlanCard } from '../components/CustomPlanCard';
import { ExternalLinkIcon } from '../components/ExternalLinkIcon';
import { useSaveCard } from '../components/RpcUi';
import { useAuth } from '../lib/auth';
import { DOCS_URL } from '../lib/chains';
import { isIntentPlan } from '../lib/intent';
import {
  compact,
  count,
  formatDate,
  formatDateTime,
  parseDollars,
  price,
  signedUsd,
  usd,
} from '../lib/money';
import { proofPriceRows, usePrices, type PriceList } from '../lib/prices';
import {
  ledgerKindLabel,
  meteredBy,
  rpcApi,
  type LedgerEntry,
  type OpenLine,
  type RateCard,
  type RpcPlan,
  type RpcSummary,
} from '../lib/rpc-client';
import { SaasError } from '../lib/saas-client';

// [PAUL: preset credit pack sizes. Only the $10 minimum is in the spec; saas
// allows any whole-cent amount from $10 to $5,000.]
const CREDIT_PRESETS = [10_000_000, 50_000_000, 100_000_000];

function errText(e: unknown, fallback: string): string {
  return e instanceof SaasError ? e.message : fallback;
}

/** Stripe Checkout and the billing portal are full-page redirects. */
function go(url: string | undefined) {
  if (url) window.location.assign(url);
}

function planLines(p: RpcPlan): string[] {
  // Pay as you go has no allowance of its own, so it has no line for one.
  const included = p.included_units > 0;
  const lines = [
    ...(included ? [`${compact(p.included_units)} units a month`] : []),
    `${count(p.requests_per_second)} requests a second`,
  ];
  if (p.monthly_micros === 0 && p.unit_price_micros_per_million != null) {
    lines.push(
      `${price(p.unit_price_micros_per_million)} per 1M units${included ? ' past that' : ''}, from prepaid credit`,
    );
  }
  if (p.proof_credit_micros > 0) lines.push(`${price(p.proof_credit_micros)} of proof credit a month`);
  if (p.overage_allowed && p.unit_price_micros_per_million != null) {
    lines.push(`Overage ${price(p.unit_price_micros_per_million)} per 1M units, if you opt in`);
  }
  lines.push(p.proofs ? 'Proofs on request, per proven block' : 'No proofs');
  return lines;
}

function PlanCards({
  prices,
  summary,
  picked,
  onChanged,
}: {
  prices: PriceList;
  summary: RpcSummary;
  picked: string | null;
  onChanged: () => Promise<void>;
}) {
  const { user } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = summary.plan.id;
  const hasSubscription = summary.plan_source !== 'none';

  async function subscribe(p: RpcPlan, funding: 'card' | 'balance') {
    if (
      funding === 'balance' &&
      !confirm(
        `Pay for ${p.display_name} from your balance? ${price(p.monthly_micros)} is taken now and again at each renewal while your balance covers it.`,
      )
    )
      return;
    setBusy(`${p.id}-${funding}`);
    setError(null);
    try {
      const res = await rpcApi.choosePlan(p.id, funding);
      if (res.url) {
        go(res.url);
        return;
      }
      await onChanged();
    } catch (e) {
      setError(errText(e, 'Could not change the plan.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="panel" data-testid="plans">
      <div className="panel-eyebrow">Plans</div>
      {picked && picked !== current && (
        <p className="rpc-picked" data-testid="picked-plan">
          You picked <strong>{prices.plans.find((p) => p.id === picked)?.display_name ?? picked}</strong> on the pricing page.
          {picked === 'payg' ? ' Add credit below to start.' : ' Subscribe below to start.'}
        </p>
      )}
      <div className="rpc-plan-grid">
        {prices.plans.map((p) => {
          const isCurrent = p.id === current;
          const sub = p.monthly_micros > 0;
          return (
            <div
              key={p.id}
              className={`rpc-plan${isCurrent ? ' is-current' : ''}${picked === p.id ? ' is-picked' : ''}`}
              data-testid={`plan-${p.id}`}
            >
              <div className="rpc-plan-name">
                {p.display_name}
                {isCurrent && <span className="rpc-badge">Current</span>}
              </div>
              <div className="rpc-plan-price">
                {sub ? (
                  <>
                    {price(p.monthly_micros)}
                    <span>/month</span>
                  </>
                ) : p.id === 'free' ? (
                  <>
                    $0<span>/month</span>
                  </>
                ) : (
                  <>
                    {price(p.unit_price_micros_per_million ?? 0)}
                    <span>/1M units</span>
                  </>
                )}
              </div>
              <ul className="rpc-plan-lines">
                {planLines(p).map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
              <div className="rpc-plan-actions">
                {sub && !isCurrent && (
                  <>
                    <button
                      className="btn btn-primary btn-sm"
                      type="button"
                      disabled={!!busy || hasSubscription}
                      onClick={() => subscribe(p, 'card')}
                      data-testid={`subscribe-${p.id}-card`}
                    >
                      {busy === `${p.id}-card` ? 'Opening Stripe…' : `Subscribe with card`}
                    </button>
                    {summary.balance.cash_micros >= p.monthly_micros && summary.plan_source !== 'stripe' && (
                      <button
                        className="btn btn-sm"
                        type="button"
                        disabled={!!busy}
                        onClick={() => subscribe(p, 'balance')}
                        data-testid={`subscribe-${p.id}-balance`}
                      >
                        Pay from balance
                      </button>
                    )}
                  </>
                )}
                {p.id === 'payg' && !isCurrent && (
                  <a className="btn btn-sm" href="#credits">
                    Add credit to start
                  </a>
                )}
              </div>
            </div>
          );
        })}
        {/* An account on a custom plan already has one (and sees no plan cards). */}
        {!summary.plan.postpaid && (
          <CustomPlanCard email={user?.email ?? ''} account={user?.email ?? summary.account_id} />
        )}
      </div>
      {hasSubscription && (
        <p className="muted rpc-small">
          To move between Builder and Scale, cancel the current plan here (it runs to the end
          of the period) or change it in the Stripe billing portal.
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
    </section>
  );
}

function CurrentPlan({ summary, onChanged }: { summary: RpcSummary; onChanged: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const s = summary;
  const subscribed = s.plan_source !== 'none';
  const postpaid = s.plan.postpaid === true;

  async function onCancelOrResume() {
    const cancel = !s.cancel_at_period_end;
    if (cancel && !confirm(`Cancel ${s.plan.display_name}? It stays active until ${formatDate(s.period.end)}.`))
      return;
    setBusy(true);
    setError(null);
    try {
      await (cancel ? rpcApi.cancelPlan() : rpcApi.resumePlan());
      await onChanged();
    } catch (e) {
      setError(errText(e, 'Could not update the subscription.'));
    } finally {
      setBusy(false);
    }
  }

  async function onPortal() {
    setBusy(true);
    setError(null);
    try {
      go((await rpcApi.billingPortal()).url);
    } catch (e) {
      setError(
        e instanceof SaasError && e.status === 400
          ? 'There are no card payments on this account yet.'
          : errText(e, 'Could not open the billing portal.'),
      );
      setBusy(false);
    }
  }

  return (
    <section className="panel" data-testid="current-plan">
      <div className="panel-eyebrow">Current plan</div>
      <div className="rpc-current">
        <div>
          <h2>{s.plan.display_name}</h2>
          <p className="muted">
            {postpaid
              ? 'Invoiced monthly'
              : s.plan_source === 'stripe'
                ? 'Card subscription'
                : s.plan_source === 'balance'
                  ? 'Paid from your balance each month'
                  : 'No subscription'}{' '}
            · period {formatDate(s.period.start)} to {formatDate(s.period.end)}
            {s.cancel_at_period_end && (
              <>
                {' '}
                · <strong>ends {formatDate(s.period.end)}</strong>
              </>
            )}
          </p>
        </div>
        <div className="rpc-current-actions">
          {subscribed && (
            <button className="btn btn-sm" type="button" onClick={onCancelOrResume} disabled={busy} data-testid="cancel-resume">
              {s.cancel_at_period_end ? 'Keep the plan' : 'Cancel plan'}
            </button>
          )}
          <button className="btn btn-sm" type="button" onClick={onPortal} disabled={busy} data-testid="billing-portal">
            Cards and invoices
          </button>
        </div>
      </div>
      {postpaid ? (
        <PostpaidFacts summary={s} />
      ) : (
        <dl className="rpc-facts">
          <div>
            <dt>Units</dt>
            <dd>
              {count(s.units.used)}
              {s.units.included > 0 && <> of {count(s.units.included)}</>} used
            </dd>
          </div>
          <div>
            <dt>Balance</dt>
            <dd>{usd(s.balance.cash_micros)}</dd>
          </div>
          <div>
            <dt>Proof credit</dt>
            <dd>{usd(s.balance.proof_credit_micros)} left this period</dd>
          </div>
          <div>
            <dt>Spent from balance this period</dt>
            <dd>{usd(s.caps.period_spend_micros)}</dd>
          </div>
        </dl>
      )}
      {error && <p className="form-error">{error}</p>}
    </section>
  );
}

/** A postpaid plan's price and what this period has run up so far. */
function PostpaidFacts({ summary: s }: { summary: RpcSummary }) {
  const per = meteredBy(s.plan);
  const byGroup = (s.rates?.groups.length ?? 0) > 0;
  return (
    <dl className="rpc-facts" data-testid="postpaid-facts">
      <div>
        <dt>Price</dt>
        <dd>
          {byGroup ? (
            <>By method group, below</>
          ) : (
            <>
              {price(s.plan.unit_price_micros_per_million ?? 0)} per 1M {per}
            </>
          )}
        </dd>
      </div>
      <div>
        <dt>{per === 'requests' ? 'Requests' : 'Units'}</dt>
        <dd>{count(s.units.used)} this period</dd>
      </div>
      <div>
        <dt>Usage this period</dt>
        <dd>
          {usd(s.caps.period_spend_micros)}, invoiced {formatDate(s.period.end)}
        </dd>
      </div>
    </dl>
  );
}

/** A plan priced by method group: each group's price and methods, then the default. */
function PlanRates({ rates }: { rates: RateCard }) {
  const per = rates.unit;
  return (
    <section className="panel" data-testid="plan-rates">
      <div className="panel-eyebrow">Prices by method group</div>
      <p className="panel-description">
        Each request is priced by the group its method is in. A method in none of these is
        priced as anything else. Refused requests are free.
      </p>
      <div className="rpc-table-wrap">
        <table className="key-table rpc-rates">
          <thead>
            <tr>
              <th>Group</th>
              <th>Methods</th>
              <th>Per 1M {per}</th>
            </tr>
          </thead>
          <tbody>
            {rates.groups.map((g) => (
              <tr key={g.id}>
                <td>
                  {g.id}. {g.name}
                </td>
                <td className="rpc-methods">
                  {g.methods.length > 0 ? g.methods.join(', ') : <span className="muted">{g.note || '—'}</span>}
                </td>
                <td>{price(g.micros_per_million)}</td>
              </tr>
            ))}
            {rates.default_micros_per_million != null && (
              <tr>
                <td>Anything else</td>
                <td className="muted">Methods in no group above</td>
                <td>{price(rates.default_micros_per_million)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** A postpaid plan's usage not invoiced yet, by price: next invoice's lines so far. */
function Uninvoiced({ summary: s }: { summary: RpcSummary }) {
  const owed = s.uninvoiced;
  if (!owed || owed.lines.length === 0) return null;
  const byGroup = (s.rates?.groups.length ?? 0) > 0;
  const label = (l: OpenLine) =>
    l.group_id ? `${l.group_id}. ${l.name ?? l.group_id}` : byGroup ? `Other ${l.unit}` : `All ${l.unit}`;
  return (
    <section className="panel" data-testid="uninvoiced">
      <div className="panel-eyebrow">Not invoiced yet</div>
      <p className="panel-description">
        Usage since the last invoice, as the next one will list it. It is invoiced when the
        period ends on {formatDate(s.period.end)}.
      </p>
      <div className="rpc-table-wrap">
        <table className="key-table rpc-num-table">
          <thead>
            <tr>
              <th />
              <th>Quantity</th>
              <th>Per 1M</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {owed.lines.map((l) => (
              <tr key={`${l.group_id ?? ''}:${l.micros_per_million}:${l.unit}`}>
                <td>{label(l)}</td>
                <td>
                  {count(l.quantity)} {l.unit}
                </td>
                <td>{price(l.micros_per_million)}</td>
                <td>{usd(l.charged_micros)}</td>
              </tr>
            ))}
            <tr className="rpc-total-row">
              <td>Total</td>
              <td />
              <td />
              <td>{usd(owed.charged_micros)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Postpaid plans: the card the monthly invoice is charged to, in place of
 *  credit, caps, and plan cards (saas refuses caps and plan changes there). */
function InvoiceCard({ summary }: { summary: RpcSummary }) {
  const { busy, error, save } = useSaveCard();
  const onFile = summary.billing.card_on_file;

  return (
    <section className="panel" data-testid="invoice-card">
      <div className="panel-eyebrow">Card</div>
      <p className="panel-description">
        Your plan is invoiced monthly. Each period's usage is billed when the period ends
        and charged to the card on file. Credit, spend caps, and plan changes don't apply.
        To change the plan, <a href="mailto:info@willow.tech">contact us</a>.
      </p>
      <div className="rpc-current">
        <p data-testid="card-status">
          {onFile ? (
            <>
              <strong>Card on file.</strong> Invoices are charged to it.
            </>
          ) : (
            <>
              <strong>No card on file.</strong>{' '}
              {summary.state === 'card_required' ? (
                <>Your keys don't work until you add one.</>
              ) : (
                <>Add one before this period ends on {formatDate(summary.period.end)}.</>
              )}
            </>
          )}
        </p>
        <button
          className={onFile ? 'btn btn-sm' : 'btn btn-primary btn-sm'}
          type="button"
          disabled={busy}
          onClick={save}
          data-testid="save-card"
        >
          {busy ? 'Opening Stripe…' : onFile ? 'Replace card' : 'Add a card'}
        </button>
      </div>
      {error && <p className="form-error">{error}</p>}
    </section>
  );
}

function Credits({ prices }: { prices: PriceList }) {
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const min = prices.credits.min_topup_micros ?? 10_000_000;
  const max = prices.credits.max_topup_micros ?? 5_000_000_000;

  async function buy(micros: number) {
    if (micros < min || micros > max) {
      setError(`Choose an amount from ${price(min)} to ${price(max)}.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      go((await rpcApi.buyCredits(micros)).url);
    } catch (e) {
      setError(errText(e, 'Could not open checkout.'));
      setBusy(false);
    }
  }

  function onCustom(e: FormEvent) {
    e.preventDefault();
    const micros = parseDollars(amount);
    if (micros == null) {
      setError('Enter an amount in dollars, like 25 or 25.50.');
      return;
    }
    buy(micros);
  }

  return (
    <section className="panel" id="credits" data-testid="credits">
      <div className="panel-eyebrow">Add credit</div>
      <p className="panel-description">
        Prepaid credit pays for units past your plan and for proofs. Pay by card through
        Stripe.
        {prices.credits.x402 && (
          <>
            {' '}
            Agents can also top up with USDC over x402 (
            <a href={`${DOCS_URL}/guides/agents/`} target="_blank" rel="noreferrer">
              how agents <ExternalLinkIcon>pay</ExternalLinkIcon>
            </a>
            ).
          </>
        )}
      </p>
      <div className="rpc-credit-row">
        {CREDIT_PRESETS.map((m) => (
          <button
            key={m}
            className="btn"
            type="button"
            disabled={busy}
            onClick={() => buy(m)}
            data-testid={`credit-${m / 1_000_000}`}
          >
            Add {price(m)}
          </button>
        ))}
        <form className="rpc-credit-custom" onSubmit={onCustom}>
          <span aria-hidden="true">$</span>
          <input
            inputMode="decimal"
            placeholder="Other amount"
            aria-label="Other amount in dollars"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="credit-custom"
          />
          <button className="btn btn-primary" type="submit" disabled={busy} data-testid="credit-custom-buy">
            Add credit
          </button>
        </form>
      </div>
      {error && <p className="form-error">{error}</p>}
    </section>
  );
}

function SpendCap({ summary, onChanged }: { summary: RpcSummary; onChanged: () => Promise<void> }) {
  const caps = summary.caps;
  const [cap, setCap] = useState(caps.spend_cap_micros == null ? '' : String(caps.spend_cap_micros / 1_000_000));
  const [overage, setOverage] = useState(caps.overage_enabled);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const overageAllowed = summary.plan.overage_allowed && summary.plan_source === 'stripe';

  useEffect(() => {
    setCap(caps.spend_cap_micros == null ? '' : String(caps.spend_cap_micros / 1_000_000));
    setOverage(caps.overage_enabled);
  }, [caps.spend_cap_micros, caps.overage_enabled]);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    const micros = cap.trim() === '' ? null : parseDollars(cap);
    if (cap.trim() !== '' && micros == null) {
      setError('Enter the cap in dollars, like 100, or leave it empty for no cap.');
      return;
    }
    if (overage && micros == null) {
      setError('Overage needs a spend cap.');
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await rpcApi.putCaps({ spend_cap_micros: micros, overage_enabled: overage });
      await onChanged();
      setSaved(true);
    } catch (err) {
      setError(errText(err, 'Could not save.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel" data-testid="spend-cap">
      <div className="panel-eyebrow">Spend cap and overage</div>
      <form onSubmit={onSave} className="rpc-cap-form">
        <label className="rpc-field">
          <span>Monthly spend cap</span>
          <div className="rpc-money-input">
            <span aria-hidden="true">$</span>
            <input
              inputMode="decimal"
              placeholder="No cap"
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              data-testid="cap-input"
            />
          </div>
          <small>
            The most this account spends from its balance in a period, on units and proofs.
            Past it, requests get HTTP 402 until the next period. Spent so far:{' '}
            {usd(caps.period_spend_micros)}.
          </small>
        </label>
        <label className={`checkbox-row${overageAllowed ? '' : ' is-disabled'}`}>
          <input
            type="checkbox"
            checked={overage}
            disabled={!overageAllowed}
            onChange={(e) => setOverage(e.target.checked)}
            data-testid="overage-toggle"
          />
          <span>
            Keep serving past a zero balance, up to the cap, and bill my card afterwards
            {summary.plan.unit_price_micros_per_million != null && summary.plan.overage_allowed && (
              <> ({price(summary.plan.unit_price_micros_per_million)} per 1M units)</>
            )}
            .
            {!overageAllowed && (
              <span className="muted"> Overage is for Builder and Scale card subscriptions.</span>
            )}
          </span>
        </label>
        {error && <p className="form-error">{error}</p>}
        {saved && !error && <p className="form-success">Saved.</p>}
        <div className="rpc-form-actions">
          <button className="btn btn-primary btn-sm" type="submit" disabled={busy} data-testid="cap-save">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </section>
  );
}

function Ledger({ postpaid = false }: { postpaid?: boolean }) {
  const [entries, setEntries] = useState<LedgerEntry[] | null>(null);
  const [next, setNext] = useState<number | null>(null);
  const [unposted, setUnposted] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (before: number | null) => {
    setBusy(true);
    setError(null);
    try {
      const res = await rpcApi.ledger({ limit: 25, before_id: before });
      setEntries((prev) => (before == null ? res.entries : [...(prev ?? []), ...res.entries]));
      setNext(res.next_before_id);
      setUnposted(res.unposted_usage_micros);
    } catch (e) {
      setError(errText(e, 'Could not load the ledger.'));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load(null);
  }, [load]);

  return (
    <section className="panel" data-testid="ledger">
      <div className="panel-eyebrow">{postpaid ? 'Usage and payments' : 'Balance history'}</div>
      {unposted > 0 && (
        <p className="muted rpc-small" data-testid="unposted">
          Recent usage not itemized yet: {usd(unposted)}.{' '}
          {postpaid ? "It's already in what you owe" : "It's already taken from your balance"} and
          shows here as one usage line within the hour.
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
      {entries === null ? (
        <p className="muted">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="muted">
          {postpaid
            ? 'Nothing yet. Usage and invoice payments show here.'
            : 'Nothing yet. Credit, plan fees, usage past your plan, and proofs show here.'}
        </p>
      ) : (
        <div className="rpc-table-wrap">
          <table
            className={`key-table rpc-num-table rpc-ledger${postpaid ? ' rpc-ledger--owed' : ''}`}
            data-testid="ledger-table"
          >
            <thead>
              <tr>
                <th>When</th>
                <th>What</th>
                {!postpaid && <th>From</th>}
                <th>Amount</th>
                <th>{postpaid ? 'Owed after' : 'Balance after'}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td>{formatDateTime(e.created_at)}</td>
                  <td>
                    {postpaid && e.kind === 'stripe_overage_payment' ? 'Invoice paid' : ledgerKindLabel(e.kind)}
                  </td>
                  {!postpaid && <td>{e.pocket === 'proof_credit' ? 'Proof credit' : 'Balance'}</td>}
                  <td className={e.amount_micros < 0 ? 'rpc-debit' : 'rpc-credit'}>{signedUsd(e.amount_micros)}</td>
                  <td>{usd(postpaid ? -e.balance_after_micros : e.balance_after_micros)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {next != null && (
        <button className="btn btn-sm" type="button" disabled={busy} onClick={() => load(next)} data-testid="ledger-more">
          {busy ? 'Loading…' : 'Load more'}
        </button>
      )}
    </section>
  );
}

function ProofPrices({ prices }: { prices: PriceList }) {
  return (
    <section className="panel" data-testid="proof-prices">
      <div className="panel-eyebrow">Proof prices</div>
      <p className="panel-description">
        A proof is charged per block in its range, at one flat price per chain, whether the
        block is already proven or not. You never pay twice for the same block, and a job that
        fails is refunded. Your plan's proof credit is used first.
      </p>
      <div className="rpc-table-wrap">
        <table className="key-table rpc-num-table">
          <thead>
            <tr>
              <th>Chain</th>
              <th>Per proven block</th>
              <th>Per 1,000 blocks</th>
            </tr>
          </thead>
          <tbody>
            {proofPriceRows(prices).map((r) => (
              <tr key={r.chainId}>
                <td>{r.chain}</td>
                <td>{r.perBlock}</td>
                <td>{r.perThousandBlocks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function BillingPanel() {
  const { summary, reloadSummary } = useAccount();
  const prices = usePrices();
  const [params] = useSearchParams();
  const raw = params.get('plan');
  const picked = isIntentPlan(raw) ? raw : null;

  if (!summary) return <p className="muted">Loading…</p>;
  if (summary.plan.postpaid) {
    return (
      <>
        <CurrentPlan summary={summary} onChanged={reloadSummary} />
        {summary.rates && summary.rates.groups.length > 0 && <PlanRates rates={summary.rates} />}
        <Uninvoiced summary={summary} />
        <InvoiceCard summary={summary} />
        {summary.plan.proofs && <ProofPrices prices={prices} />}
        <Ledger postpaid />
      </>
    );
  }
  return (
    <>
      <CurrentPlan summary={summary} onChanged={reloadSummary} />
      <PlanCards prices={prices} summary={summary} picked={picked} onChanged={reloadSummary} />
      <Credits prices={prices} />
      <SpendCap summary={summary} onChanged={reloadSummary} />
      <ProofPrices prices={prices} />
      <Ledger />
    </>
  );
}
